import { Alert } from "bootstrap";

export default class Form {
  constructor(formSelector) {
    this.form = document.querySelector(formSelector);
    this.submitListeners = [];

    this.form.addEventListener("submit", (event) =>
      this.formSubmitListener(event)
    );

    // Dateigrößen/Anzahl sofort nach einer Auswahl prüfen.
    this.form.querySelectorAll('input[type="file"]').forEach((input) => {
      input.addEventListener("change", () => {
        this.validateFiles(input);
      });
    });

    const isAjaxForm = "formAjax" in this.form.dataset;

    this.defaultSubmit = !isAjaxForm;

    if (isAjaxForm) {
      this.submitListeners.push(this.ajaxSubmit);
    }
  }

  get(propertyName) {
    if (propertyName === "element") {
      return this.getElement();
    }

    if (propertyName === "data") {
      return this.getData();
    }

    if (propertyName === "formData") {
      return this.getFormData();
    }

    if (propertyName === "method") {
      return this.getMethod();
    }

    if (propertyName === "action") {
      return this.getAction();
    }

    if (propertyName === "enctype") {
      return this.getEnctype();
    }

    return {
      element: this.getElement(),
      data: this.getData(),
      formData: this.getFormData(),
      method: this.getMethod(),
      action: this.getAction(),
      enctype: this.getEnctype(),
    };
  }

  getElement() {
    return this.form;
  }

  getData(urlEncoded = false) {
    let data = {};

    this.getElement()
      .querySelectorAll("input, textarea")
      .forEach((el) => {
        data[el.name] = el.value;
      });

    if (urlEncoded) {
      const urlEncodedDataPairs = [];

      for (const [key, value] of Object.entries(data)) {
        urlEncodedDataPairs.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        );
      }

      data = urlEncodedDataPairs.join("&").replace(/%20/g, "+");
    }

    return data;
  }

  getFormData() {
    // Native FormData behandelt multiple Dateien, Hidden Inputs,
    // Selects, Textareas und Checkboxen korrekt.
    return new FormData(this.getElement());
  }

  getMethod() {
    return this.getElement().getAttribute("method");
  }

  getAction() {
    return this.getElement().getAttribute("action");
  }

  getEnctype() {
    return this.getElement().getAttribute("enctype");
  }

  getDataset(name = "") {
    const elementDataset = this.getElement().dataset;

    if (name.length > 0) {
      return elementDataset[name];
    }

    return elementDataset;
  }

  hasAlert() {
    return this.getDataset("formAlert") !== undefined;
  }

  disable() {
    this.getElement()
      .querySelectorAll("fieldset")
      .forEach((fieldset) => {
        fieldset.setAttribute("disabled", "");
        fieldset.classList.add("disabled");
      });
  }

  enable() {
    this.getElement()
      .querySelectorAll("fieldset")
      .forEach((fieldset) => {
        fieldset.removeAttribute("disabled");
        fieldset.classList.remove("disabled");
      });
  }

  reset() {
    this.getElement().reset();

    this.getElement()
      .querySelectorAll('input[type="file"]')
      .forEach((input) => {
        input.setCustomValidity("");
      });
  }

  addClass(classname) {
    this.getElement().classList.add(classname);
  }

  removeClass(classname) {
    this.getElement().classList.remove(classname);
  }

  preventDefaultSubmit() {
    this.defaultSubmit = false;
  }

  onSubmit(listener) {
    this.submitListeners.push(listener);
  }

  removeListener(listenerToRemove) {
    this.submitListeners = this.submitListeners.filter(
      (listener) => listener !== listenerToRemove
    );
  }

  emitSubmit(event) {
    this.submitListeners.forEach((callback) => {
      callback(event, this);
    });
  }

  formatBytes(bytes) {
    if (bytes >= 1024 ** 3) {
      return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
    }

    if (bytes >= 1024 ** 2) {
      return `${(bytes / 1024 ** 2).toFixed(2)} MB`;
    }

    if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }

    return `${bytes} Bytes`;
  }

  /**
   * Prüft eine File-Input-Auswahl gegen die im Template gesetzten
   * data-* Limits.
   *
   * Erwartete Attribute:
   * - data-max-files
   * - data-max-file-size
   * - data-max-total-size
   *
   * Ein Wert von 0 bedeutet: kein eigenes Browser-Limit.
   */
  validateFiles(fileInput = null) {
    const input =
      fileInput ||
      this.getElement().querySelector('input[type="file"]');

    if (!input) {
      return {
        valid: true,
        message: null,
      };
    }

    const files = Array.from(input.files || []);

    const maxFiles = Number(input.dataset.maxFiles || 0);
    const maxFileSize = Number(input.dataset.maxFileSize || 0);
    const maxTotalSize = Number(input.dataset.maxTotalSize || 0);

    let message = null;

    if (maxFiles > 0 && files.length > maxFiles) {
      message = `Es können maximal ${maxFiles} Dateien gleichzeitig hochgeladen werden.`;
    }

    if (!message && maxFileSize > 0) {
      const oversizedFile = files.find(
        (file) => file.size > maxFileSize
      );

      if (oversizedFile) {
        message =
          `Die Datei „${oversizedFile.name}“ ist zu groß. ` +
          `Erlaubt sind maximal ${this.formatBytes(maxFileSize)} pro Datei.`;
      }
    }

    if (!message && maxTotalSize > 0) {
      const totalSize = files.reduce(
        (sum, file) => sum + file.size,
        0
      );

      if (totalSize > maxTotalSize) {
        message =
          "Die ausgewählten Dateien sind insgesamt zu groß. " +
          `Erlaubt sind maximal ${this.formatBytes(maxTotalSize)}.`;
      }
    }

    input.setCustomValidity(message || "");

    return {
      valid: message === null,
      message,
    };
  }

  validate() {
    // Vor checkValidity() die eigenen Datei-Limits setzen.
    this.validateFiles();

    const formIsValid = this.getElement().checkValidity();

    if (formIsValid) {
      this.removeClass("was-validated");
    } else {
      this.addClass("was-validated");
    }

    return formIsValid;
  }

  formSubmitListener(event) {
    const valid = this.validate();

    if (valid) {
      this.emitSubmit(event);
    } else {
      event.stopPropagation();
    }

    if (!this.defaultSubmit) {
      event.preventDefault();

      return false;
    }
  }

  addInputChangeListener(name) {
    const input = this.form.querySelector(`input[name="${name}"]`);

    if (!input) {
      return;
    }

    input.addEventListener("change", () => {
      if (input.type === "file") {
        this.validateFiles(input);
      }
    });
  }

  ajaxSubmit(event, form) {
    form.disable();

    const xhr = new XMLHttpRequest();

    xhr.onreadystatechange = function () {
      if (this.readyState !== 4) {
        return;
      }

      form.enable();

      if (this.status !== 200) {
        return;
      }

      const contentType = xhr.getResponseHeader("content-type") || "";

      if (!contentType.includes("json")) {
        return;
      }

      const { error, success } = JSON.parse(xhr.response);

      const triggerAlert = (
        alertId,
        alertContent = null,
        alertFunction = null
      ) => {
        const alertTargetEl = document.getElementById(alertId);

        if (!alertTargetEl) {
          return false;
        }

        const alertEl = alertTargetEl.cloneNode(true);
        alertEl.id = "";

        alertTargetEl.parentNode.append(alertEl);

        if (alertContent !== null) {
          alertEl.getElementsByTagName("div")[0].textContent = alertContent;
        }

        const bsAlert = new Alert(alertEl);

        alertEl.classList.remove("visually-hidden");

        setTimeout(() => {
          alertEl.classList.add("show");
        }, 100);

        const alertAutoclose = alertEl.dataset?.alertAutoclose;

        if (alertAutoclose) {
          setTimeout(() => {
            bsAlert.close();
          }, alertAutoclose);
        }

        if (alertFunction !== null) {
          alertFunction();
        }

        return true;
      };

      if (error === false) {
        if (success) {
          triggerAlert(form.getDataset("formAlertSuccess"), success);
        }

        const ajaxSuccessFunction = form.getDataset("formAjaxSuccess");

        if (ajaxSuccessFunction === "reload") {
          window.location.reload();
        } else if (ajaxSuccessFunction?.startsWith("redirect:")) {
          window.location.replace(ajaxSuccessFunction.substring(9));
        }
      } else {
        const errorAlertTriggered = triggerAlert(
          form.getDataset("formAlertError"),
          error
        );

        if (!errorAlertTriggered) {
          console.warn(error);
        }
      }

      form.reset();
    };

    xhr.onloadend = function () {
      form.enable();
    };

    xhr.open(form.getMethod(), form.getAction(), true);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.send(form.getFormData());
  }
}